import React, {useEffect} from "react";
import {data, useNavigate, useParams} from "react-router-dom";
import {type TournamentForm} from '@iziping/common';
import styles from "@/styles/form.module.css";
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import type {ApiError} from '@iziping/common';
import {useGetTournament, useSaveTournament} from "../hooks/tournamentHooks";
import { useNotification } from '@iziping/ui';

const TournamentPage: React.FC = () => {
    const { id } = useParams();
    const tournamentId = id ? parseInt(id, 10) : undefined;
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { notify } = useNotification();
    const { data: tournament, isLoading, isError } = useGetTournament(tournamentId);

    const { register, handleSubmit, reset, formState: { errors } } = useForm<TournamentForm>({
        defaultValues: {
            name: '',
            location: '',
            nbTables: 1,
            maxHeat: 1,
            date: new Date(),
        },
    });

    useEffect(() => {
        if (tournament) {
            reset({
                name: tournament.name,
                location: tournament.location,
                nbTables: tournament.nbTables,
                maxHeat: tournament.maxHeat,
                date: tournament.date
            });
        }
        else {
            reset({ name: '', location: '', nbTables: 1, maxHeat: 1, date: new Date() });
        }
    }, [tournament, reset]);

    const saveMutation = useSaveTournament();

    const onSubmit = async (data: TournamentForm) => {
        try {
            const payload = tournament ? { id: tournament.id, data } : { data };
            await saveMutation.mutateAsync(payload);
            notify(t('tournament.created.success'), 'success');
            if (!tournamentId) navigate('/');
        } catch (err: unknown) {
            const apiError = err as ApiError;
            notify(apiError?.error || 'Erreur inconnue', 'error');
        }
    };

    if (isLoading) return <div>Loading...</div>;
    if (isError) {
        console.log("error : ", data)
        return <div>Error fetching data</div>;
    }
    return (
        <div>
            <form className={styles.formContainer}  onSubmit={handleSubmit(onSubmit)}>
                <h2>{`${ tournamentId ? t('tournament.editing'): t('tournament.creating')}`}</h2>
                <div className={styles.formGroup}>
                    <label htmlFor="name">{t('tournament.name')}</label>
                    <input
                        id="name"
                        className={styles.input}
                        {...register('name', { required: t('tournament.name.required') })}
                    />
                    {errors.name && <span className={styles.error}>{errors.name.message}</span>}
                </div>
                <div className={styles.formGroup}>
                    <label htmlFor="points">{t('tournament.location')}</label>
                    <input id="location" type="text" className={styles.input} {...register('location')} />
                </div>
                <div className={styles.formGroup}>
                    <label htmlFor="date">{t('tournament.date')}</label>
                    <input id="date" type="date" className={styles.input}
                        {...register('date', {
                            required: t('tournament.date.required'),
                            valueAsDate: true
                        })}
                    />
                    {errors.date && (
                        <span className={styles.error}>{errors.date.message}</span>
                    )}
                </div>
                <div className={styles.formGroup}>
                    <label htmlFor="tableNumber">{t('tournament.tablesNumber')}</label>
                    <input id="tableNumber" type="number" className={styles.input} {...register('nbTables')} />
                </div>
                <div className={styles.formGroup}>
                    <label htmlFor="maxHeat">{t('tournament.maxHeat')}</label>
                    <input id="maxHeat" type="number" className={styles.input} {...register('maxHeat')} />
                </div>

                <div className={styles.actionsContainer}>
                    <button type="button" className={styles.deleteButton} onClick={() => navigate(-1 as any)}>
                        {t('tournament.cancel.button')}
                    </button>
                    <button type="submit">
                        {tournament ? t('tournament.update.button') : t('tournament.create.button')}
                    </button>
                </div>

            </form>
        </div>


    );
};

export default TournamentPage;